'use client';
// useDragScroll — mouse drag-to-scroll for a horizontal strip (Founder 2026-09-22: "גלילה לצדדים כמו סליידר של
// קרוסלה עם העכבר שמזיזים" — reviews strip and the work-photo carousel). Touch is left to the element's own
// handling (native pan-x / ReviewsList's touch driver); this only claims the MOUSE. A drag longer than 6px
// swallows the click that would otherwise fire on release (so dragging over a card/photo never opens it).
import { useEffect, type RefObject } from 'react';

export function useDragScroll(ref: RefObject<HTMLElement | null>, enabled = true) {
  useEffect(() => {
    const el = ref.current;
    if (!el || !enabled) return;
    let down = false, moved = false, startX = 0, startLeft = 0, lastX = 0, lastT = 0, vx = 0, raf: number | null = null;
    // a scroll-snap strip (components/ReviewSlider) re-snaps every programmatic scrollLeft — snap is held off for the drag + momentum,
    // then restored, which lets the browser settle on the nearest slide
    let snapPrev: string | null = null;
    const snapOff = () => { if (snapPrev === null) { snapPrev = el.style.scrollSnapType; el.style.scrollSnapType = 'none'; } };
    const snapOn = () => { if (snapPrev !== null) { el.style.scrollSnapType = snapPrev; snapPrev = null; } };
    el.style.cursor = 'grab';
    const stop = () => { if (raf !== null) { cancelAnimationFrame(raf); raf = null; } };
    const onDown = (e: PointerEvent) => {
      if (e.pointerType !== 'mouse' || e.button !== 0) return;
      stop(); down = true; moved = false; startX = lastX = e.clientX; startLeft = el.scrollLeft; lastT = performance.now(); vx = 0;
      el.style.cursor = 'grabbing'; el.style.userSelect = 'none';
    };
    const onMove = (e: PointerEvent) => {
      if (!down) return;
      const dx = e.clientX - startX;
      if (!moved && Math.abs(dx) > 6) moved = true;
      if (moved) { snapOff(); el.scrollLeft = startLeft - dx; e.preventDefault(); }
      const now = performance.now(); vx = (e.clientX - lastX) / Math.max(1, now - lastT); lastX = e.clientX; lastT = now;
    };
    const onUp = () => {
      if (!down) return;
      down = false; el.style.cursor = 'grab'; el.style.userSelect = '';
      // momentum, like the touch driver
      let v = vx; const step = () => { if (Math.abs(v) < 0.02) { raf = null; snapOn(); return; } el.scrollLeft -= v * 16; v *= 0.92; raf = requestAnimationFrame(step); };
      if (moved && Math.abs(v) > 0.05) raf = requestAnimationFrame(step); else snapOn();
    };
    const onClick = (e: MouseEvent) => { if (moved) { e.stopPropagation(); e.preventDefault(); moved = false; } };
    // a native image/link drag (starts after a few px on <img>/<a>) fires pointercancel and kills the scroll — block it
    const onDragStart = (e: DragEvent) => e.preventDefault();
    el.addEventListener('pointerdown', onDown); window.addEventListener('pointermove', onMove); window.addEventListener('pointerup', onUp); window.addEventListener('pointercancel', onUp); el.addEventListener('click', onClick, true); el.addEventListener('dragstart', onDragStart);
    return () => { stop(); el.removeEventListener('pointerdown', onDown); window.removeEventListener('pointermove', onMove); window.removeEventListener('pointerup', onUp); window.removeEventListener('pointercancel', onUp); el.removeEventListener('click', onClick, true); el.removeEventListener('dragstart', onDragStart); };
  }, [ref, enabled]);
}
